import React, { useState } from "react";
import {
  confirmResetPassword,
  confirmSignIn,
  resetPassword,
  signIn,
  signInWithRedirect,
} from "aws-amplify/auth";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

const CODE_STEPS = new Set([
  "CONFIRM_SIGN_IN_WITH_SMS_CODE",
  "CONFIRM_SIGN_IN_WITH_TOTP_CODE",
]);

export default function CognitoSignIn({
  onAuthenticated,
  onSwitchToSignUp,
  allowFederated = false,
  federatedReturnPath,
  requireTerms = false,
  title,
  description,
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challengeValue, setChallengeValue] = useState("");
  const [mode, setMode] = useState("sign-in");
  const [termsAccepted, setTermsAccepted] = useState(!requireTerms);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const advanceSignIn = async (result) => {
    const nextStep = result?.nextStep?.signInStep;
    if (result?.isSignedIn || nextStep === "DONE") {
      await onAuthenticated();
      return;
    }
    if (nextStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
      setChallengeValue("");
      setMode("new-password");
      return;
    }
    if (CODE_STEPS.has(nextStep)) {
      setChallengeValue("");
      setMode("mfa");
      return;
    }
    throw new Error("This sign-in verification method is not supported.");
  };

  const submitSignIn = async (event) => {
    event.preventDefault();
    if (requireTerms && !termsAccepted) {
      setError(
        "Please accept the Terms of Use and Privacy Policy to continue."
      );
      return;
    }
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const result = await signIn({
        username: email.trim(),
        password,
        options: { authFlowType: "USER_SRP_AUTH" },
      });
      await advanceSignIn(result);
    } catch (signInError) {
      setError(signInError?.message || "Unable to sign in.");
    } finally {
      setBusy(false);
    }
  };

  const submitChallenge = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await confirmSignIn({ challengeResponse: challengeValue });
      await advanceSignIn(result);
    } catch (challengeError) {
      setError(challengeError?.message || "Unable to complete sign-in.");
    } finally {
      setBusy(false);
    }
  };

  const sendResetCode = async () => {
    if (!email.trim()) {
      setError("Enter your email first.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await resetPassword({ username: email.trim() });
      setChallengeValue("");
      setPassword("");
      setMode("reset");
      setMessage("A password reset code was sent to your email.");
    } catch (resetError) {
      setError(resetError?.message || "Unable to send a reset code.");
    } finally {
      setBusy(false);
    }
  };

  const submitResetPassword = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await confirmResetPassword({
        username: email.trim(),
        confirmationCode: challengeValue,
        newPassword: password,
      });
      setMode("sign-in");
      setChallengeValue("");
      setPassword("");
      setMessage("Password reset. You can now sign in.");
    } catch (resetError) {
      setError(resetError?.message || "Unable to reset your password.");
    } finally {
      setBusy(false);
    }
  };

  const federatedSignIn = async () => {
    if (requireTerms && !termsAccepted) {
      setError(
        "Please accept the Terms of Use and Privacy Policy to continue."
      );
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (federatedReturnPath) {
        sessionStorage.setItem("cognito_return_path", federatedReturnPath);
      } else {
        sessionStorage.removeItem("cognito_return_path");
      }
      await signInWithRedirect({ provider: "Google" });
    } catch (federatedError) {
      sessionStorage.removeItem("cognito_return_path");
      setError(federatedError?.message || "Unable to sign in with Google.");
      setBusy(false);
    }
  };

  if (mode === "new-password" || mode === "mfa") {
    return (
      <Stack component="form" spacing={2} onSubmit={submitChallenge}>
        <Typography variant="h6">
          {mode === "new-password"
            ? "Choose a new password"
            : "Verification required"}
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          label={mode === "new-password" ? "New password" : "Verification code"}
          type={mode === "new-password" ? "password" : "text"}
          autoComplete={
            mode === "new-password" ? "new-password" : "one-time-code"
          }
          inputProps={mode === "mfa" ? { inputMode: "numeric" } : undefined}
          value={challengeValue}
          onChange={(event) => setChallengeValue(event.target.value)}
          required
        />
        <Button type="submit" variant="contained" disabled={busy}>
          Continue
        </Button>
      </Stack>
    );
  }

  if (mode === "reset") {
    return (
      <Stack component="form" spacing={2} onSubmit={submitResetPassword}>
        <Typography variant="h6">Reset password</Typography>
        {message && <Alert severity="info">{message}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        <TextField label="Email" value={email} disabled />
        <TextField
          label="Reset code"
          autoComplete="one-time-code"
          value={challengeValue}
          onChange={(event) => setChallengeValue(event.target.value)}
          required
        />
        <TextField
          label="New password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
        />
        <Button type="submit" variant="contained" disabled={busy}>
          Reset password
        </Button>
        <Button onClick={() => setMode("sign-in")} disabled={busy}>
          Back to sign in
        </Button>
      </Stack>
    );
  }

  return (
    <Stack component="form" spacing={2} onSubmit={submitSignIn}>
      {title && <Typography variant="h5">{title}</Typography>}
      {description && (
        <Typography color="text.secondary">{description}</Typography>
      )}
      {message && <Alert severity="success">{message}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
      <TextField
        label="Email"
        type="email"
        autoComplete="username"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        required
      />
      <TextField
        label="Password"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        required
      />
      <Box>
        <Button size="small" onClick={sendResetCode} disabled={busy}>
          Forgot password?
        </Button>
      </Box>
      {requireTerms && (
        <FormControlLabel
          control={
            <Checkbox
              checked={termsAccepted}
              onChange={(event) => setTermsAccepted(event.target.checked)}
            />
          }
          label={
            <Typography variant="body2">
              I agree to the{" "}
              <a
                href="https://mhome.ai/terms-of-use"
                target="_blank"
                rel="noreferrer"
              >
                Terms of Use
              </a>{" "}
              and{" "}
              <a
                href="https://mhome.ai/privacy-policy"
                target="_blank"
                rel="noreferrer"
              >
                Privacy Policy
              </a>
            </Typography>
          }
        />
      )}
      <Button type="submit" variant="contained" disabled={busy}>
        Sign in
      </Button>
      {allowFederated && (
        <Button variant="outlined" onClick={federatedSignIn} disabled={busy}>
          Sign in with Google
        </Button>
      )}
      {onSwitchToSignUp && (
        <Typography variant="body2" align="center">
          Need an account?{" "}
          <Button size="small" onClick={onSwitchToSignUp} disabled={busy}>
            Sign up
          </Button>
        </Typography>
      )}
    </Stack>
  );
}
